/**
 * HostForm Component
 *
 * Form for creating and editing hosts with:
 * - Name input (slug format identifier)
 * - Hostname input (IP or domain)
 * - SSH connection settings (port, username, SSH key path)
 * - Claude CLI path configuration
 * - Skills and workspace base paths
 * - Enable/disable toggle
 *
 * @module components/admin/HostForm
 */

import { useState, useCallback, useMemo, useEffect, memo } from 'react';
import {
  Server,
  Network,
  Key,
  Terminal,
  FolderOpen,
  FileText,
  Save,
  X,
  Loader2,
  AlertCircle,
  Check,
  ChevronDown,
  Power,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';

// ============================================
// Constants
// ============================================

/**
 * Validation constraints matching backend Zod schemas
 */
const VALIDATION = {
  NAME: {
    MIN: 1,
    MAX: 64,
    PATTERN: /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/
  },
  HOSTNAME: {
    MIN: 1,
    MAX: 253
  },
  PORT: {
    MIN: 1,
    MAX: 65535
  },
  USERNAME: {
    MIN: 1,
    MAX: 64
  },
  DESCRIPTION: {
    MAX: 1024
  },
  PATH: {
    PATTERN: /^\//
  }
};

/**
 * Default form values matching backend CreateHostSchema defaults
 */
const DEFAULT_FORM_VALUES = {
  name: '',
  hostname: '',
  port: 22,
  username: 'claude',
  description: '',
  sshKeyPath: '',
  claudePath: '/usr/local/bin/claude',
  skillsPath: '/shared/skills',
  workspacePath: '/home',
  isEnabled: true
};

/**
 * Suggest Claude CLI path based on username
 * @param {string} username - SSH username
 * @returns {string} Suggested Claude path
 */
function suggestClaudePath(username) {
  if (!username || username === 'root') {
    return '/usr/local/bin/claude';
  }
  // User-local installation path (npm install -g claude as user)
  return `/home/${username}/.local/bin/claude`;
}

/**
 * Validation check status types
 */
const CHECK_STATUS = {
  PENDING: 'pending',
  SUCCESS: 'success',
  FAILED: 'failed',
  ERROR: 'error'
};

// ============================================
// Validation Functions
// ============================================

/**
 * Validate host name (slug format)
 * @param {string} value - Name to validate
 * @returns {string|null} Error message or null if valid
 */
function validateName(value) {
  if (!value || value.trim().length === 0) {
    return 'Name is required';
  }
  if (value.length > VALIDATION.NAME.MAX) {
    return `Name must be ${VALIDATION.NAME.MAX} characters or less`;
  }
  if (!VALIDATION.NAME.PATTERN.test(value)) {
    return 'Name must be lowercase alphanumeric with optional hyphens (not at start/end for multi-char names)';
  }
  return null;
}

/**
 * Validate hostname (IP or domain)
 * @param {string} value - Hostname to validate
 * @returns {string|null} Error message or null if valid
 */
function validateHostname(value) {
  if (!value || value.trim().length === 0) {
    return 'Hostname is required';
  }
  if (value.length > VALIDATION.HOSTNAME.MAX) {
    return `Hostname must be ${VALIDATION.HOSTNAME.MAX} characters or less`;
  }
  return null;
}

/**
 * Validate port number
 * @param {number|string} value - Port to validate
 * @returns {string|null} Error message or null if valid
 */
function validatePort(value) {
  const port = parseInt(value, 10);
  if (isNaN(port)) {
    return 'Port must be a number';
  }
  if (port < VALIDATION.PORT.MIN || port > VALIDATION.PORT.MAX) {
    return `Port must be between ${VALIDATION.PORT.MIN} and ${VALIDATION.PORT.MAX}`;
  }
  return null;
}

/**
 * Validate SSH username
 * @param {string} value - Username to validate
 * @returns {string|null} Error message or null if valid
 */
function validateUsername(value) {
  if (!value || value.trim().length === 0) {
    return 'Username is required';
  }
  if (value.length > VALIDATION.USERNAME.MAX) {
    return `Username must be ${VALIDATION.USERNAME.MAX} characters or less`;
  }
  return null;
}

/**
 * Validate description
 * @param {string} value - Description to validate
 * @returns {string|null} Error message or null if valid
 */
function validateDescription(value) {
  if (value && value.length > VALIDATION.DESCRIPTION.MAX) {
    return `Description must be ${VALIDATION.DESCRIPTION.MAX} characters or less`;
  }
  return null;
}

/**
 * Validate path (must be absolute)
 * @param {string} value - Path to validate
 * @param {boolean} required - Whether the path is required
 * @returns {string|null} Error message or null if valid
 */
function validatePath(value, required = false) {
  if (!value || value.trim().length === 0) {
    return required ? 'Path is required' : null;
  }
  if (!VALIDATION.PATH.PATTERN.test(value)) {
    return 'Path must be absolute (start with /)';
  }
  return null;
}

/**
 * Validate Claude path
 * @param {string} value - Path to validate
 * @returns {string|null} Error message or null if valid
 */
function validateClaudePath(value) {
  return validatePath(value, true);
}

/**
 * Validate skills path
 * @param {string} value - Path to validate
 * @returns {string|null} Error message or null if valid
 */
function validateSkillsPath(value) {
  return validatePath(value, true);
}

/**
 * Validate workspace path
 * @param {string} value - Path to validate
 * @returns {string|null} Error message or null if valid
 */
function validateWorkspacePath(value) {
  return validatePath(value, true);
}

/**
 * Validate SSH key path (optional)
 * @param {string} value - Path to validate
 * @returns {string|null} Error message or null if valid
 */
function validateSshKeyPath(value) {
  return validatePath(value, false);
}

/**
 * Validate entire form
 * @param {object} values - Form values
 * @returns {object} Errors object with field keys
 */
function validateForm(values) {
  return {
    name: validateName(values.name),
    hostname: validateHostname(values.hostname),
    port: validatePort(values.port),
    username: validateUsername(values.username),
    description: validateDescription(values.description),
    sshKeyPath: validateSshKeyPath(values.sshKeyPath),
    claudePath: validateClaudePath(values.claudePath),
    skillsPath: validateSkillsPath(values.skillsPath),
    workspacePath: validateWorkspacePath(values.workspacePath)
  };
}

/**
 * Check if form has any errors
 * @param {object} errors - Errors object
 * @returns {boolean} True if form has errors
 */
function hasErrors(errors) {
  return Object.values(errors).some((error) => error !== null);
}

// ============================================
// Sub-Components
// ============================================

/**
 * Form field wrapper with label and error display
 * @param {object} props - Component props
 * @param {string} props.id - Input ID
 * @param {string} props.label - Field label
 * @param {boolean} [props.required] - Whether field is required
 * @param {string} [props.error] - Error message
 * @param {string} [props.hint] - Hint text
 * @param {React.ReactNode} [props.icon] - Field icon
 * @param {React.ReactNode} props.children - Input element
 * @returns {React.ReactElement} Form field
 */
function FormField({ id, label, required, error, hint, icon: Icon, children }) {
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={id}
        className="flex items-center gap-2 text-sm font-medium text-gray-200"
      >
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
        {label}
        {required && <span className="text-red-400">*</span>}
      </label>
      {children}
      {error ? (
        <p className="flex items-center gap-1 text-xs text-red-400">
          <AlertCircle className="w-3 h-3" />
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-gray-500">{hint}</p>
      ) : null}
    </div>
  );
}

/**
 * Text input component
 * @param {object} props - Component props
 * @returns {React.ReactElement} Input element
 */
function TextInput({
  id,
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  hasError,
  disabled,
  type = 'text',
  maxLength
}) {
  return (
    <input
      type={type}
      id={id}
      name={name}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      placeholder={placeholder}
      disabled={disabled}
      maxLength={maxLength}
      className={`
        w-full px-3 py-2 rounded-md
        bg-gray-900 border text-gray-100 placeholder-gray-500
        focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
        disabled:opacity-50 disabled:cursor-not-allowed
        transition-colors
        ${hasError
          ? 'border-red-500 focus:ring-red-500'
          : 'border-gray-700 hover:border-gray-600'
        }
      `}
    />
  );
}

/**
 * Number input component
 * @param {object} props - Component props
 * @returns {React.ReactElement} Number input element
 */
function NumberInput({
  id,
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  hasError,
  disabled,
  min,
  max
}) {
  return (
    <input
      type="number"
      id={id}
      name={name}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      placeholder={placeholder}
      disabled={disabled}
      min={min}
      max={max}
      className={`
        w-full px-3 py-2 rounded-md
        bg-gray-900 border text-gray-100 placeholder-gray-500
        focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
        disabled:opacity-50 disabled:cursor-not-allowed
        transition-colors
        ${hasError
          ? 'border-red-500 focus:ring-red-500'
          : 'border-gray-700 hover:border-gray-600'
        }
      `}
    />
  );
}

/**
 * Textarea component
 * @param {object} props - Component props
 * @returns {React.ReactElement} Textarea element
 */
function TextArea({
  id,
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  hasError,
  disabled,
  rows = 3,
  maxLength
}) {
  const charCount = value?.length || 0;
  const showCharCount = maxLength && charCount > maxLength * 0.8;

  return (
    <div className="relative">
      <textarea
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        maxLength={maxLength}
        className={`
          w-full px-3 py-2 rounded-md resize-none
          bg-gray-900 border text-gray-100 placeholder-gray-500
          focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
          disabled:opacity-50 disabled:cursor-not-allowed
          transition-colors
          ${hasError
            ? 'border-red-500 focus:ring-red-500'
            : 'border-gray-700 hover:border-gray-600'
          }
        `}
      />
      {showCharCount && (
        <span
          className={`absolute bottom-2 right-2 text-xs ${
            charCount >= maxLength ? 'text-red-400' : 'text-gray-500'
          }`}
        >
          {charCount}/{maxLength}
        </span>
      )}
    </div>
  );
}

/**
 * Toggle switch component
 * @param {object} props - Component props
 * @param {string} props.id - Input ID
 * @param {string} props.name - Input name
 * @param {boolean} props.checked - Whether toggle is checked
 * @param {Function} props.onChange - Change handler
 * @param {boolean} [props.disabled] - Whether toggle is disabled
 * @param {string} [props.label] - Toggle label
 * @returns {React.ReactElement} Toggle switch
 */
function Toggle({ id, name, checked, onChange, disabled, label }) {
  return (
    <label
      htmlFor={id}
      className={`
        flex items-center gap-3 cursor-pointer
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
    >
      <div className="relative">
        <input
          type="checkbox"
          id={id}
          name={name}
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className="sr-only peer"
        />
        <div
          className={`
            w-11 h-6 rounded-full
            bg-gray-700 peer-focus:ring-2 peer-focus:ring-indigo-500
            peer-checked:bg-indigo-600
            transition-colors
          `}
        />
        <div
          className={`
            absolute left-0.5 top-0.5 w-5 h-5 rounded-full
            bg-white shadow-sm
            peer-checked:translate-x-5
            transition-transform
          `}
        />
      </div>
      {label && (
        <span className="text-sm text-gray-200">{label}</span>
      )}
    </label>
  );
}

/**
 * Form action buttons
 * @param {object} props - Component props
 * @param {Function} props.onSubmit - Submit handler
 * @param {Function} props.onCancel - Cancel handler
 * @param {boolean} props.isSubmitting - Whether form is submitting
 * @param {boolean} props.isValid - Whether form is valid
 * @param {string} [props.submitLabel] - Submit button label
 * @returns {React.ReactElement} Action buttons
 */
function FormActions({ onSubmit, onCancel, isSubmitting, isValid, submitLabel = 'Create Host' }) {
  return (
    <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-700">
      <button
        type="button"
        onClick={onCancel}
        disabled={isSubmitting}
        className="
          flex items-center gap-2 px-4 py-2 rounded-md
          text-gray-300 bg-gray-700 hover:bg-gray-600
          focus:outline-none focus:ring-2 focus:ring-gray-500
          disabled:opacity-50 disabled:cursor-not-allowed
          transition-colors
        "
      >
        <X className="w-4 h-4" />
        Cancel
      </button>
      <button
        type="submit"
        onClick={onSubmit}
        disabled={isSubmitting || !isValid}
        className="
          flex items-center gap-2 px-4 py-2 rounded-md
          text-white bg-indigo-600 hover:bg-indigo-500
          focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-800
          disabled:opacity-50 disabled:cursor-not-allowed
          transition-colors
        "
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <Save className="w-4 h-4" />
            {submitLabel}
          </>
        )}
      </button>
    </div>
  );
}

/**
 * Success message component
 * @param {object} props - Component props
 * @param {string} props.message - Success message
 * @param {Function} props.onDismiss - Dismiss handler
 * @returns {React.ReactElement} Success message
 */
function SuccessMessage({ message, onDismiss }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-md bg-green-500/10 border border-green-500/30">
      <div className="flex items-center gap-2 text-green-400">
        <Check className="w-4 h-4" />
        <span className="text-sm">{message}</span>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="p-1 rounded hover:bg-green-500/20 text-green-400"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

/**
 * Error message component
 * @param {object} props - Component props
 * @param {string} props.message - Error message
 * @param {Function} props.onDismiss - Dismiss handler
 * @returns {React.ReactElement} Error message
 */
function ErrorMessage({ message, onDismiss }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-md bg-red-500/10 border border-red-500/30">
      <div className="flex items-center gap-2 text-red-400">
        <AlertCircle className="w-4 h-4" />
        <span className="text-sm">{message}</span>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="p-1 rounded hover:bg-red-500/20 text-red-400"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

/**
 * Connection validation results component
 * @param {object} props - Component props
 * @param {object} props.results - Validation results object
 * @param {boolean} props.isValidating - Whether validation is in progress
 * @param {Function} props.onValidate - Validate button handler
 * @returns {React.ReactElement} Validation results
 */
const ValidationResults = memo(function ValidationResults({
  results,
  isValidating,
  onValidate
}) {
  if (!results && !isValidating) {
    return (
      <button
        type="button"
        onClick={onValidate}
        className="
          flex items-center gap-2 px-3 py-2 rounded-md
          text-gray-300 bg-gray-700 hover:bg-gray-600
          focus:outline-none focus:ring-2 focus:ring-gray-500
          transition-colors text-sm
        "
      >
        <RefreshCw className="w-4 h-4" />
        Test Connection
      </button>
    );
  }

  if (isValidating) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-md bg-gray-800 border border-gray-700">
        <Loader2 className="w-4 h-4 text-indigo-400 animate-spin" />
        <span className="text-sm text-gray-300">Validating connection...</span>
      </div>
    );
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case CHECK_STATUS.SUCCESS:
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case CHECK_STATUS.FAILED:
        return <XCircle className="w-4 h-4 text-red-400" />;
      case CHECK_STATUS.ERROR:
        return <AlertCircle className="w-4 h-4 text-yellow-400" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case CHECK_STATUS.SUCCESS:
        return 'text-green-400';
      case CHECK_STATUS.FAILED:
        return 'text-red-400';
      case CHECK_STATUS.ERROR:
        return 'text-yellow-400';
      default:
        return 'text-gray-400';
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-200">Validation Results</h4>
        <button
          type="button"
          onClick={onValidate}
          className="
            flex items-center gap-1 px-2 py-1 rounded
            text-gray-400 hover:text-white hover:bg-gray-700
            transition-colors text-xs
          "
        >
          <RefreshCw className="w-3 h-3" />
          Retest
        </button>
      </div>

      <div className="p-3 rounded-md bg-gray-800 border border-gray-700 space-y-2">
        {results.checks && Object.entries(results.checks).map(([check, result]) => (
          <div key={check} className="flex items-start gap-2">
            {getStatusIcon(result.status)}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-200 capitalize">
                  {check.replace(/([A-Z])/g, ' $1').trim()}
                </span>
                <span className={`text-xs ${getStatusColor(result.status)}`}>
                  {result.status}
                </span>
              </div>
              {result.message && (
                <p className="text-xs text-gray-400 mt-0.5">{result.message}</p>
              )}
            </div>
          </div>
        ))}

        <div className="pt-2 mt-2 border-t border-gray-700">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-200">Overall:</span>
            <span className={`text-sm ${results.isValid ? 'text-green-400' : 'text-red-400'}`}>
              {results.isValid ? 'Valid' : 'Invalid'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
});

/**
 * Form section header component
 * @param {object} props - Component props
 * @param {string} props.title - Section title
 * @param {string} [props.description] - Section description
 * @returns {React.ReactElement} Section header
 */
function SectionHeader({ title, description }) {
  return (
    <div className="pb-2 mb-4 border-b border-gray-700/50">
      <h3 className="text-sm font-semibold text-gray-200">{title}</h3>
      {description && (
        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
      )}
    </div>
  );
}

// ============================================
// Main Component
// ============================================

/**
 * HostForm Component
 *
 * Form for creating and editing hosts with validation and submission handling.
 *
 * @param {object} props - Component props
 * @param {object} [props.initialValues] - Initial form values for editing
 * @param {Function} props.onSubmit - Form submission handler (receives form values)
 * @param {Function} [props.onCancel] - Cancel button handler
 * @param {Function} [props.onSuccess] - Success callback
 * @param {Function} [props.onValidate] - Validate connection handler (receives connection params, returns results)
 * @param {string} [props.submitLabel] - Custom submit button label
 * @param {boolean} [props.isEditing] - Whether form is in edit mode
 * @param {string} [props.className] - Additional CSS classes
 * @returns {React.ReactElement} Host form
 */
export default function HostForm({
  initialValues,
  onSubmit,
  onCancel,
  onSuccess,
  onValidate,
  submitLabel,
  isEditing = false,
  className = ''
}) {
  // ----------------------------------------
  // State
  // ----------------------------------------

  const [values, setValues] = useState(() => ({
    ...DEFAULT_FORM_VALUES,
    ...initialValues
  }));
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  // ----------------------------------------
  // Computed Values
  // ----------------------------------------

  /**
   * Check if form is valid (has values and no errors)
   */
  const isFormValid = useMemo(() => {
    const formErrors = validateForm(values);
    return !hasErrors(formErrors);
  }, [values]);

  // ----------------------------------------
  // Handlers
  // ----------------------------------------

  /**
   * Handle input change
   */
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setValues((prev) => {
      const updated = { ...prev, [name]: newValue };

      // Auto-suggest claudePath when username changes (only if claudePath hasn't been manually edited)
      if (name === 'username') {
        const suggestedPath = suggestClaudePath(newValue);
        const currentIsDefault = prev.claudePath === DEFAULT_FORM_VALUES.claudePath ||
                                  prev.claudePath === suggestClaudePath(prev.username);
        if (currentIsDefault) {
          updated.claudePath = suggestedPath;
        }
      }

      return updated;
    });
    setSubmitError(null);
    setSubmitSuccess(null);

    // Clear validation results when connection params change
    if (['hostname', 'port', 'username', 'sshKeyPath'].includes(name)) {
      setValidationResults(null);
    }
  }, []);

  /**
   * Handle input blur (mark as touched)
   */
  const handleBlur = useCallback((e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    // Validate on blur
    const fieldValidators = {
      name: validateName,
      hostname: validateHostname,
      port: validatePort,
      username: validateUsername,
      description: validateDescription,
      sshKeyPath: validateSshKeyPath,
      claudePath: validateClaudePath,
      skillsPath: validateSkillsPath,
      workspacePath: validateWorkspacePath
    };

    const validator = fieldValidators[name];
    if (validator) {
      setErrors((prev) => ({
        ...prev,
        [name]: validator(values[name])
      }));
    }
  }, [values]);

  /**
   * Handle connection validation
   */
  const handleValidate = useCallback(async () => {
    if (!onValidate) return;

    setIsValidating(true);
    setValidationResults(null);

    try {
      const results = await onValidate({
        hostname: values.hostname.trim(),
        port: parseInt(values.port, 10) || 22,
        username: values.username.trim() || 'claude',
        sshKeyPath: values.sshKeyPath?.trim() || null
      });
      setValidationResults(results);
    } catch (error) {
      setValidationResults({
        isValid: false,
        checks: {
          connection: {
            status: CHECK_STATUS.ERROR,
            message: error.message || 'Validation failed'
          }
        }
      });
    } finally {
      setIsValidating(false);
    }
  }, [values.hostname, values.port, values.username, values.sshKeyPath, onValidate]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(
    async (e) => {
      e?.preventDefault();

      // Validate all fields
      const formErrors = validateForm(values);
      setErrors(formErrors);
      setTouched({
        name: true,
        hostname: true,
        port: true,
        username: true,
        description: true,
        sshKeyPath: true,
        claudePath: true,
        skillsPath: true,
        workspacePath: true
      });

      if (hasErrors(formErrors)) {
        return;
      }

      setIsSubmitting(true);
      setSubmitError(null);
      setSubmitSuccess(null);

      try {
        // Clean up values before submission
        const submissionValues = {
          name: values.name.trim(),
          hostname: values.hostname.trim(),
          port: parseInt(values.port, 10),
          username: values.username.trim(),
          claudePath: values.claudePath.trim(),
          skillsPath: values.skillsPath.trim(),
          workspacePath: values.workspacePath.trim(),
          isEnabled: values.isEnabled,
          ...(values.description?.trim() && { description: values.description.trim() }),
          ...(values.sshKeyPath?.trim() && { sshKeyPath: values.sshKeyPath.trim() })
        };

        await onSubmit(submissionValues);

        setSubmitSuccess(
          isEditing
            ? 'Host updated successfully'
            : 'Host created successfully'
        );

        // Reset form if creating (not editing)
        if (!isEditing) {
          setValues(DEFAULT_FORM_VALUES);
          setTouched({});
          setErrors({});
          setValidationResults(null);
        }

        onSuccess?.();
      } catch (error) {
        const message =
          error.response?.data?.message ||
          error.message ||
          'Failed to save host. Please try again.';
        setSubmitError(message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [values, onSubmit, onSuccess, isEditing]
  );

  /**
   * Handle cancel button
   */
  const handleCancel = useCallback(() => {
    setValues({ ...DEFAULT_FORM_VALUES, ...initialValues });
    setTouched({});
    setErrors({});
    setSubmitError(null);
    setSubmitSuccess(null);
    setValidationResults(null);
    onCancel?.();
  }, [initialValues, onCancel]);

  // ----------------------------------------
  // Effects
  // ----------------------------------------

  // Update form when initialValues change (for editing)
  useEffect(() => {
    if (initialValues) {
      setValues((prev) => ({ ...prev, ...initialValues }));
    }
  }, [initialValues]);

  // ----------------------------------------
  // Render
  // ----------------------------------------

  return (
    <form
      onSubmit={handleSubmit}
      className={`space-y-6 ${className}`}
      noValidate
    >
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b border-gray-700">
        <div className="p-2 rounded-lg bg-emerald-500/10">
          <Server className="w-6 h-6 text-emerald-400" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-white">
            {isEditing ? 'Edit Host' : 'Add New Host'}
          </h2>
          <p className="text-sm text-gray-400">
            {isEditing
              ? 'Update the host configuration'
              : 'Configure a new host for running agents'}
          </p>
        </div>
      </div>

      {/* Status Messages */}
      {submitSuccess && (
        <SuccessMessage
          message={submitSuccess}
          onDismiss={() => setSubmitSuccess(null)}
        />
      )}
      {submitError && (
        <ErrorMessage
          message={submitError}
          onDismiss={() => setSubmitError(null)}
        />
      )}

      {/* Form Fields */}
      <div className="space-y-6">
        {/* Basic Information Section */}
        <div>
          <SectionHeader
            title="Basic Information"
            description="Host identification and description"
          />
          <div className="space-y-4">
            {/* Name */}
            <FormField
              id="name"
              label="Name"
              required
              icon={Server}
              error={touched.name ? errors.name : null}
              hint="Unique identifier (lowercase alphanumeric with hyphens)"
            >
              <TextInput
                id="name"
                name="name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="e.g., dev-server, prod-host-1"
                hasError={touched.name && errors.name}
                disabled={isSubmitting || isEditing}
                maxLength={VALIDATION.NAME.MAX}
              />
            </FormField>

            {/* Description */}
            <FormField
              id="description"
              label="Description"
              icon={FileText}
              error={touched.description ? errors.description : null}
              hint="Brief description of this host's purpose"
            >
              <TextArea
                id="description"
                name="description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="e.g., Development server for the web team"
                hasError={touched.description && errors.description}
                disabled={isSubmitting}
                rows={2}
                maxLength={VALIDATION.DESCRIPTION.MAX}
              />
            </FormField>
          </div>
        </div>

        {/* SSH Connection Section */}
        <div>
          <SectionHeader
            title="SSH Connection"
            description="Connection settings for accessing the host"
          />
          <div className="space-y-4">
            {/* Hostname */}
            <FormField
              id="hostname"
              label="Hostname"
              required
              icon={Network}
              error={touched.hostname ? errors.hostname : null}
              hint="IP address or domain name"
            >
              <TextInput
                id="hostname"
                name="hostname"
                value={values.hostname}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="e.g., 192.168.1.100 or server.example.com"
                hasError={touched.hostname && errors.hostname}
                disabled={isSubmitting}
                maxLength={VALIDATION.HOSTNAME.MAX}
              />
            </FormField>

            {/* Port and Username (side by side) */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                id="port"
                label="Port"
                required
                error={touched.port ? errors.port : null}
              >
                <NumberInput
                  id="port"
                  name="port"
                  value={values.port}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="22"
                  hasError={touched.port && errors.port}
                  disabled={isSubmitting}
                  min={VALIDATION.PORT.MIN}
                  max={VALIDATION.PORT.MAX}
                />
              </FormField>

              <FormField
                id="username"
                label="Username"
                required
                error={touched.username ? errors.username : null}
              >
                <TextInput
                  id="username"
                  name="username"
                  value={values.username}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="claude"
                  hasError={touched.username && errors.username}
                  disabled={isSubmitting}
                  maxLength={VALIDATION.USERNAME.MAX}
                />
              </FormField>
            </div>

            {/* SSH Key Path */}
            <FormField
              id="sshKeyPath"
              label="SSH Key Path"
              icon={Key}
              error={touched.sshKeyPath ? errors.sshKeyPath : null}
              hint="Path to SSH private key on this machine (optional, leave blank for default)"
            >
              <TextInput
                id="sshKeyPath"
                name="sshKeyPath"
                value={values.sshKeyPath}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="e.g., /home/user/.ssh/id_rsa"
                hasError={touched.sshKeyPath && errors.sshKeyPath}
                disabled={isSubmitting}
              />
            </FormField>

            {/* Connection Validation */}
            {onValidate && (
              <ValidationResults
                results={validationResults}
                isValidating={isValidating}
                onValidate={handleValidate}
              />
            )}
          </div>
        </div>

        {/* Path Configuration Section */}
        <div>
          <SectionHeader
            title="Path Configuration"
            description="File system paths on the remote host"
          />
          <div className="space-y-4">
            {/* Claude Path */}
            <FormField
              id="claudePath"
              label="Claude CLI Path"
              required
              icon={Terminal}
              error={touched.claudePath ? errors.claudePath : null}
              hint={`Path to Claude CLI on host (auto-suggested for user "${values.username}")`}
            >
              <TextInput
                id="claudePath"
                name="claudePath"
                value={values.claudePath}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="/usr/local/bin/claude"
                hasError={touched.claudePath && errors.claudePath}
                disabled={isSubmitting}
              />
            </FormField>

            {/* Skills Path */}
            <FormField
              id="skillsPath"
              label="Skills Base Path"
              required
              icon={FolderOpen}
              error={touched.skillsPath ? errors.skillsPath : null}
              hint="Base directory for shared skills on the host"
            >
              <TextInput
                id="skillsPath"
                name="skillsPath"
                value={values.skillsPath}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="/shared/skills"
                hasError={touched.skillsPath && errors.skillsPath}
                disabled={isSubmitting}
              />
            </FormField>

            {/* Workspace Path */}
            <FormField
              id="workspacePath"
              label="Workspace Base Path"
              required
              icon={FolderOpen}
              error={touched.workspacePath ? errors.workspacePath : null}
              hint="Base directory for agent workspaces on the host"
            >
              <TextInput
                id="workspacePath"
                name="workspacePath"
                value={values.workspacePath}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="/home"
                hasError={touched.workspacePath && errors.workspacePath}
                disabled={isSubmitting}
              />
            </FormField>
          </div>
        </div>

        {/* Settings Section */}
        <div>
          <SectionHeader
            title="Settings"
            description="Host status configuration"
          />
          <div className="space-y-4">
            {/* Enable Toggle */}
            <div className="flex items-center justify-between p-3 rounded-md bg-gray-800/50 border border-gray-700/50">
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-gray-200">
                  <Power className="w-4 h-4 text-gray-400" />
                  Host Enabled
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  Disabled hosts cannot run new agent sessions
                </p>
              </div>
              <Toggle
                id="isEnabled"
                name="isEnabled"
                checked={values.isEnabled}
                onChange={handleChange}
                disabled={isSubmitting}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <FormActions
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
        isValid={isFormValid}
        submitLabel={submitLabel || (isEditing ? 'Save Changes' : 'Create Host')}
      />
    </form>
  );
}

// ============================================
// Named Exports for Sub-Components
// ============================================

export {
  FormField,
  TextInput,
  NumberInput,
  TextArea,
  Toggle,
  FormActions,
  SuccessMessage,
  ErrorMessage,
  ValidationResults,
  SectionHeader,
  validateName,
  validateHostname,
  validatePort,
  validateUsername,
  validateDescription,
  validatePath,
  validateClaudePath,
  validateSkillsPath,
  validateWorkspacePath,
  validateSshKeyPath,
  validateForm,
  hasErrors,
  VALIDATION,
  DEFAULT_FORM_VALUES,
  CHECK_STATUS
};
